/**
 * Componente: Formulario Paso 2 — Datos de la Cuenta y Titular Legal
 * Rediseñado fielmente según el diseño HTML de Paso 2 (#70a32c)
 */

'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useForm, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Country } from 'country-state-city';
import * as Flags from 'country-flag-icons/react/3x2';
import { hasFlag } from 'country-flag-icons';
import {
  step2UsuarioFormSchema,
  Step2UsuarioFormInput,
  Step2UsuarioFormOutput,
} from '../schemas/onboarding.schema';
import { Paso2UsuarioFormData } from '../types/onboarding.types';
import { PrefijoWhatsappModalSelector, PrefijoWhatsappItem } from './PrefijoWhatsappModalSelector';
import { PorQuePedimosDatosModal } from './PorQuePedimosDatosModal';
import { TerminosCondicionesModal } from './TerminosCondicionesModal';
import styles from './Step2UsuarioForm.module.css';

function CountryFlag({ isoCode }: { isoCode: string }) {
  const upper = (isoCode || '').toUpperCase();
  if (hasFlag(upper)) {
    const FlagComp = (
      Flags as Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>>
    )[upper];
    if (FlagComp) {
      return <FlagComp className="w-full h-full object-cover" />;
    }
  }
  return <span className="text-[10px]">🇨🇴</span>;
}

interface Step2UsuarioFormProps {
  initialData: Paso2UsuarioFormData;
  onSubmit: (data: Paso2UsuarioFormData) => Promise<boolean>;
  onBack: () => void;
  onResetValidation?: () => void;
}

export function Step2UsuarioForm({
  initialData,
  onSubmit,
  onBack,
  onResetValidation,
}: Step2UsuarioFormProps) {
  const [isSubmittingLocal, setIsSubmittingLocal] = useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const [isCountryModalOpen, setIsCountryModalOpen] = useState<boolean>(false);
  const [isTerminosModalOpen, setIsTerminosModalOpen] = useState<boolean>(false);

  const [countryIso, setCountryIso] = useState<string>('CO');
  const [dialCode, setDialCode] = useState<string>(initialData.codigo_pais_whatsapp || '+57');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<Step2UsuarioFormInput, unknown, Step2UsuarioFormOutput>({
    resolver: zodResolver(step2UsuarioFormSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      primer_nombre: initialData.primer_nombre || '',
      segundo_nombre: initialData.segundo_nombre || '',
      primer_apellido: initialData.primer_apellido || '',
      segundo_apellido: initialData.segundo_apellido || '',
      whatsapp: initialData.whatsapp || '',
      codigo_pais_whatsapp: initialData.codigo_pais_whatsapp || '+57',
      email: initialData.email || '',
    },
  });

  const handleSelectPrefix = (item: PrefijoWhatsappItem) => {
    setCountryIso(item.isoCode);
    setDialCode(item.phoneCode);
    setValue('codigo_pais_whatsapp', item.phoneCode, { shouldValidate: true });
    setIsCountryModalOpen(false);
  };

  const handleFormSubmit = async (data: Step2UsuarioFormOutput) => {
    setIsSubmittingLocal(true);
    try {
      await onSubmit(data as Paso2UsuarioFormData);
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const onFormInvalid = (formErrors: FieldErrors<Step2UsuarioFormInput>) => {
    const errorKeys = Object.keys(formErrors) as (keyof Step2UsuarioFormInput)[];
    if (errorKeys.length > 0) {
      const firstKey = errorKeys[0];
      const firstMsg =
        formErrors[firstKey]?.message || 'Por favor completa todos los campos requeridos.';
      toast.error(firstMsg);
    }
  };

  return (
    <div className={styles.formWrapper}>
      {/* ─── Encabezado Principal ─── */}
      <div className="w-full text-center select-none">
        <h1
          className="m-0 leading-none text-slate-800"
          style={{
            fontFamily: "'Caveat', cursive",
            fontSize: 'clamp(34px, 4vw, 44px)',
            fontWeight: 700,
            lineHeight: 1.05,
            letterSpacing: '-0.5px',
          }}
        >
          Ahora, vamos a crear tu{' '}
          <span className={styles.highlightMarker}>
            cuenta
            <svg
              className={styles.highlightSvg}
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 100 12"
            >
              <path
                d="M2 8.5C28 2.5 72 3 98 6.5C75 9 35 11 10 9"
                opacity="0.85"
                stroke="#70a32c"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3.5"
              />
            </svg>
          </span>
        </h1>
        <p
          className="text-gray-500 text-xs sm:text-sm font-normal mt-1.5 text-center max-w-md mx-auto"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          Ingresa tus datos personales para gestionar y proteger tu granja.
        </p>
      </div>

      {/* ─── Indicador de Pasos (1: Granja [Check], 2: Cuenta [Active], 3: Seguridad) ─── */}
      <nav
        aria-label="Progreso del Registro"
        className={styles.stepperNav}
        style={{ fontFamily: 'Nunito, sans-serif' }}
      >
        <div className={styles.stepperContainer}>
          {/* Paso 1: Granja (Completado) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <motion.div
              initial={{ scale: 0.75, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 450, damping: 25 }}
              className="flex items-center justify-center rounded-full text-xs font-extrabold shadow-xs"
              style={{
                width: '28px',
                height: '28px',
                backgroundColor: '#eaf3dc',
                color: '#70a32c',
                border: '1.5px solid #70a32c',
              }}
            >
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.08, type: 'spring', stiffness: 500 }}
                className="material-symbols-outlined text-[16px] font-bold"
              >
                check
              </motion.span>
            </motion.div>
            <span className="text-xs font-bold tracking-wide" style={{ color: '#70a32c' }}>
              Granja
            </span>
          </div>

          {/* Conector 1 -> 2 (Verde completado) */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.35, ease: 'easeOut', delay: 0.05 }}
            className="h-0.5 rounded-full flex-1 max-w-[38px] origin-left"
            style={{ backgroundColor: '#70a32c' }}
          />

          {/* Paso 2: Cuenta (Activo) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 400, damping: 22, delay: 0.1 }}
              className="flex items-center justify-center rounded-full text-xs font-extrabold shadow-sm text-white"
              style={{
                width: '28px',
                height: '28px',
                backgroundColor: '#70a32c',
                boxShadow: '0 2px 8px rgba(112, 163, 44, 0.4)',
              }}
            >
              2
            </motion.div>
            <span className="text-xs font-extrabold tracking-wide" style={{ color: '#70a32c' }}>
              Cuenta
            </span>
          </div>

          {/* Conector 2 -> 3 (Gris pendiente) */}
          <div
            className="h-0.5 rounded-full flex-1 max-w-[38px]"
            style={{ backgroundColor: '#e2e8f0' }}
          />

          {/* Paso 3: Seguridad (Inactivo) */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div
              className="flex items-center justify-center rounded-full text-xs font-bold text-[#94a3b8] bg-[#f1f5f9] border border-[#e2e8f0]"
              style={{ width: '28px', height: '28px' }}
            >
              3
            </div>
            <span className="text-xs font-bold tracking-wide text-[#94a3b8]">
              Seguridad
            </span>
          </div>
        </div>
      </nav>

      {/* ─── Formulario de Datos Personales ─── */}
      <form
        onSubmit={handleSubmit(handleFormSubmit, onFormInvalid)}
        className={styles.formContainer}
        noValidate
      >
        <input type="hidden" {...register('codigo_pais_whatsapp')} />
        {/* Fila 1: Primer Nombre & Segundo Nombre (2 Columnas compactas) */}
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5">
          {/* Primer Nombre */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="first-name"
              className="text-xs font-semibold text-slate-700 tracking-wide flex items-center gap-1.5 truncate"
            >
              <span className="material-symbols-outlined text-[18px] shrink-0" style={{ color: '#70a32c' }}>
                person
              </span>
              <span className="truncate">Primer nombre</span>
            </label>
            <div className="relative">
              <input
                id="first-name"
                type="text"
                placeholder="Ej. Carlos"
                className={`${styles.inputField} ${errors.primer_nombre ? styles.inputError : ''}`}
                {...register('primer_nombre')}
              />
            </div>
            {errors.primer_nombre && (
              <span className="text-[11px] text-red-500 font-semibold px-1">
                {errors.primer_nombre.message}
              </span>
            )}
          </div>

          {/* Segundo Nombre */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="middle-name"
              className="text-xs font-semibold text-slate-700 tracking-wide flex items-center gap-1.5 truncate"
            >
              <span className="material-symbols-outlined text-[18px] shrink-0" style={{ color: '#70a32c' }}>
                person_outline
              </span>
              <span className="truncate">Segundo nombre</span>
            </label>
            <div className="relative">
              <input
                id="middle-name"
                type="text"
                placeholder="Ej. Andrés"
                className={`${styles.inputField} ${errors.segundo_nombre ? styles.inputError : ''}`}
                {...register('segundo_nombre')}
              />
            </div>
            {errors.segundo_nombre && (
              <span className="text-[11px] text-red-500 font-semibold px-1">
                {errors.segundo_nombre.message}
              </span>
            )}
          </div>
        </div>

        {/* Fila 2: Primer Apellido & Segundo Apellido (2 Columnas compactas) */}
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5">
          {/* Primer Apellido */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="last-name"
              className="text-xs font-semibold text-slate-700 tracking-wide flex items-center gap-1.5 truncate"
            >
              <span className="material-symbols-outlined text-[18px] shrink-0" style={{ color: '#70a32c' }}>
                badge
              </span>
              <span className="truncate">Primer apellido</span>
            </label>
            <div className="relative">
              <input
                id="last-name"
                type="text"
                placeholder="Ej. Gómez"
                className={`${styles.inputField} ${errors.primer_apellido ? styles.inputError : ''}`}
                {...register('primer_apellido')}
              />
            </div>
            {errors.primer_apellido && (
              <span className="text-[11px] text-red-500 font-semibold px-1">
                {errors.primer_apellido.message}
              </span>
            )}
          </div>

          {/* Segundo Apellido */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="second-last-name"
              className="text-xs font-semibold text-slate-700 tracking-wide flex items-center gap-1.5 truncate"
            >
              <span className="material-symbols-outlined text-[18px] shrink-0" style={{ color: '#70a32c' }}>
                badge
              </span>
              <span className="truncate">Segundo apellido</span>
            </label>
            <div className="relative">
              <input
                id="second-last-name"
                type="text"
                placeholder="Ej. Pérez"
                className={`${styles.inputField} ${errors.segundo_apellido ? styles.inputError : ''}`}
                {...register('segundo_apellido')}
              />
            </div>
            {errors.segundo_apellido && (
              <span className="text-[11px] text-red-500 font-semibold px-1">
                {errors.segundo_apellido.message}
              </span>
            )}
          </div>
        </div>

        {/* Fila 3: WhatsApp & Correo Electrónico */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-3.5">
          {/* WhatsApp con Selector de Código de País */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="whatsapp"
              className="text-xs font-semibold text-slate-700 tracking-wide flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[18px]" style={{ color: '#70a32c' }}>
                call
              </span>
              ¿Cuál es tu WhatsApp?
            </label>
            <div className="relative">
              <div className={`${styles.phoneInputWrapper} ${errors.whatsapp ? styles.inputError : ''}`}>
                <button
                  type="button"
                  onClick={() => setIsCountryModalOpen(true)}
                  className={styles.countryCodeBtn}
                  title="Seleccionar código de país"
                  aria-label={`Código de país actual: ${dialCode}`}
                >
                  <span className="inline-flex items-center justify-center shrink-0 w-5 h-3.5 rounded-xs overflow-hidden shadow-2xs border border-slate-200">
                    <CountryFlag isoCode={countryIso} />
                  </span>
                  <span>{dialCode}</span>
                  <span className="material-symbols-outlined text-[16px] text-slate-400 leading-none">
                    expand_more
                  </span>
                </button>
                <input
                  id="whatsapp"
                  type="tel"
                  placeholder="Ej. 300 123 4567"
                  className={styles.phoneInput}
                  {...register('whatsapp')}
                />
              </div>
            </div>
            {errors.whatsapp && (
              <span className="text-[11px] text-red-500 font-semibold px-1">
                {errors.whatsapp.message}
              </span>
            )}
          </div>

          {/* Correo Electrónico */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="email"
              className="text-xs font-semibold text-slate-700 tracking-wide flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[18px]" style={{ color: '#70a32c' }}>
                mail
              </span>
              ¿Cuál es tu correo?
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                placeholder="Ej. carlos@granjasanjose.com"
                className={`${styles.inputField} ${errors.email ? styles.inputError : ''}`}
                {...register('email')}
              />
            </div>
            {errors.email && (
              <span className="text-[11px] text-red-500 font-semibold px-1">
                {errors.email.message}
              </span>
            )}
          </div>
        </div>

        {/* Fila 4: Botones de Acción (Volver & Continuar) */}
        <div className="pt-1 flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className={styles.volverBtn}
          >
            <span className="material-symbols-outlined text-[18px] text-slate-500">
              arrow_back
            </span>
            <span>Volver</span>
          </button>

          <button
            type="submit"
            disabled={isSubmittingLocal}
            className={styles.continuarBtn}
          >
            {isSubmittingLocal ? (
              <span className="inline-flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Guardando...</span>
              </span>
            ) : (
              <>
                <span>Continuar</span>
                <span className="material-symbols-outlined text-[18px]">
                  arrow_forward
                </span>
              </>
            )}
          </button>
        </div>

        {/* Fila 5: Enlace Informativo "¿Por qué me piden estos datos?" (Idéntico a Paso 1) */}
        <div className="text-center pt-1">
          <button
            type="button"
            onClick={() => setShowInfoModal(true)}
            className="text-[11px] text-gray-500 hover:text-[#70a32c] inline-flex items-center gap-1 transition cursor-pointer select-none"
          >
            <span className="material-symbols-outlined text-[13px] text-[#70a32c]">
              help
            </span>
            <span>¿Por qué me piden estos datos?</span>
          </button>
        </div>

        {/* Fila 6: ¿Ya tienes cuenta? Inicia sesión */}
        <div className="mt-2 text-center">
          <p className="text-xs text-gray-600">
            ¿Ya tienes cuenta?{' '}
            <Link
              href="/login"
              className="text-[#70a32c] font-bold hover:underline ml-0.5"
            >
              Inicia sesión
            </Link>
          </p>
        </div>

        {/* Derechos de Autor (Visible en móvil y desktop) */}
        <div className="text-center pt-3 flex flex-col items-center gap-1 select-none">
          <p className="text-[10px] text-gray-400 font-normal tracking-wide">
            © 2026 AviFácil. Todos los derechos reservados.
          </p>
        </div>
      </form>

      {/* ─── Modal Especial de Selección de Prefijo de WhatsApp ─── */}
      <PrefijoWhatsappModalSelector
        isOpen={isCountryModalOpen}
        selectedDialCode={dialCode}
        selectedIsoCode={countryIso}
        onSelect={handleSelectPrefix}
        onClose={() => setIsCountryModalOpen(false)}
      />

      {/* ─── Modal Informativo de Datos Personales ─── */}
      <PorQuePedimosDatosModal
        isOpen={showInfoModal}
        paso={2}
        onClose={() => setShowInfoModal(false)}
      />

      {/* ─── Modal de Términos y Condiciones / Política de Privacidad ─── */}
      <TerminosCondicionesModal
        isOpen={isTerminosModalOpen}
        onClose={() => setIsTerminosModalOpen(false)}
      />
    </div>
  );
}
